import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getZonesAndRooms, deleteSubplan, deleteZone } from "../../../../api/museum";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { MuseumPlansAndZonesProps } from "../../../../types/screensTypes";
import { SubPlan, Zone } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import RoomCard from "../../../../components/cards/MuseumRoomCard";
import ZoneCard from "../../../../components/cards/MuseumZoneCard";
import DeleteModal from "../../../../components/modals/DeleteModal";
import styles from "./Architecture.module.css";
import stepStyles from "../createStep.module.css";

type PendingDelete = { kind: "zone"; id: string } | { kind: "floor"; id: string } | null;

const MuseumPlansAndZones: React.FC<MuseumPlansAndZonesProps> = ({
    navigateToNext,
    navigateToPrevious,
    navigateToContinue,
}) => {
    const { trackData } = useTrackCreation();
    const [rooms, setRooms] = useState<SubPlan[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
    const [blockDeleteOpen, setBlockDeleteOpen] = useState(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const getZonesAndRoomsByTrackId = async (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getZonesAndRooms({ token, trackId });

        if (response) {
            const roomsList: SubPlan[] = response.subPlans?.sort((a, b) => a.floorNumber - b.floorNumber) || [];
            const zonesList: Zone[] = response.subPlansZones?.sort((a, b) => a.position - b.position) || [];
            setRooms(roomsList);
            setZones(zonesList);
        }
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getZonesAndRoomsByTrackId(trackData.trackId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData?.trackId]);

    const zoneHasStories = (zoneId: string) =>
        rooms.flatMap((room) => room.clickableZones || []).some((story) => story.subPlansZonesId === zoneId);

    const requestDeleteZone = (zoneId: string) => {
        if (zoneHasStories(zoneId)) {
            setPendingDelete(null);
            setBlockDeleteOpen(true);
            return;
        }
        setPendingDelete({ kind: "zone", id: zoneId });
    };

    const requestDeleteFloor = (roomId: string) => {
        setPendingDelete({ kind: "floor", id: roomId });
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;

        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;

        if (pendingDelete.kind === "zone") {
            const response = await deleteZone({ token, trackId, zoneId: pendingDelete.id });
            if (response) {
                getZonesAndRoomsByTrackId(trackId);
            }
        } else {
            const response = await deleteSubplan({ token, trackId, subplanId: pendingDelete.id });
            if (response) {
                getZonesAndRoomsByTrackId(trackId);
            }
        }

        setPendingDelete(null);
    };

    const confirmMessage = pendingDelete?.kind === "zone" ? t("deleteZoneConfirmation") : t("deleteFloorConfirmation");

    const confirmTitle = pendingDelete?.kind === "zone" ? t("deleteZone") : t("deleteFloor");

    return (
        <div className={`step-container ${styles.root}`}>
            <TitleSubtitle
                title={t("insertYourPlans")}
                subtitle={t("addAPlanOrChooseAnExistingOneToModifyIt")}
                layout="architecture"
            />

            {rooms.length > 0 && (
                <div className={styles.grid}>
                    {rooms.map((room) => (
                        <RoomCard
                            key={room.id}
                            data={room}
                            onClick={() => navigateToNext(room.id)}
                            onDelete={() => requestDeleteFloor(room.id)}
                        />
                    ))}
                </div>
            )}

            <Button className={styles.addBtn} label={t("addANewPlan")} onClick={() => navigateToNext("null")} />

            <div className={styles.section}>
                <TitleSubtitle
                    title={t("wouldYouLikeToGroupYourStoriesByZone")}
                    subtitle={t("fromTheAppVisitorsCanAccessThemesAndAssociatedStories")}
                    layout="architecture"
                />

                {zones.length > 0 && (
                    <div className={styles.grid}>
                        {zones.map((zone) => (
                            <ZoneCard
                                key={zone.id}
                                zone={zone}
                                onClick={() => navigateToPrevious(zone.id)}
                                onDelete={() => requestDeleteZone(zone.id)}
                            />
                        ))}
                    </div>
                )}

                <Button className={styles.addBtn} label={t("addANewZone")} onClick={() => navigateToPrevious("null")} />
            </div>

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${styles.footer}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={stepStyles.continueBtn}
                    />
                </div>
            )}

            <DeleteModal
                isOpen={Boolean(pendingDelete)}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title={confirmTitle}
                message={confirmMessage}
            />

            <DeleteModal
                isOpen={blockDeleteOpen}
                onClose={() => setBlockDeleteOpen(false)}
                onConfirm={() => setBlockDeleteOpen(false)}
                infoOnly
                title={t("cannotDeleteZoneTitle")}
                message={t("cannotDeleteZoneWithStories")}
                confirmLabel={t("close")}
            />
        </div>
    );
};

export default MuseumPlansAndZones;
